import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Activity, AlertTriangle, CheckCircle2, XCircle, RefreshCw,
  ArrowDownLeft, ArrowUpRight, Clock, ChevronRight, X
} from 'lucide-react';
import { getApiUrl } from '../utils/api';

// Same-origin on production (ops.soulsanskar.com) to avoid stale-build CORS.
const API = getApiUrl();

const LANE_KEYS = [
  'razorpay_inbound',
  'lms_inbound',
  'ops_to_lms_enrollment',
  'ops_to_lms_forward',
];

const STATUS_DOT = {
  success: 'bg-emerald-500',
  warning: 'bg-amber-500',
  failed: 'bg-red-500',
};

const OVERALL_BADGE = {
  healthy: { bg: 'bg-emerald-50', text: 'text-emerald-700', label: 'All systems normal' },
  degraded: { bg: 'bg-amber-50', text: 'text-amber-700', label: 'Degraded — investigate' },
  critical: { bg: 'bg-red-50', text: 'text-red-700', label: 'Critical — action needed' },
};

const CAUSE_HINT = {
  auth_failed_check_secrets: 'Rotate or re-sync LMS_WEBHOOK_SECRET / LMS_SERVICE_KEY between OPS and LMS.',
  auth_forbidden_wrong_role_or_key: 'Service key mismatch or role check failed. Verify caller credentials.',
  idempotency_body_mismatch: 'Same idempotency key reused with a different body. Likely caller bug.',
  payload_validation_failed: 'Body shape does not match contract. Check field names + types.',
  resource_not_found: 'Target row (lead/doctor/payment) not found in OPS.',
  lms_5xx_check_lms_health: 'LMS returned 5xx. Page LMS oncall.',
  ops_5xx_internal_bug: 'OPS internal error. Check backend logs.',
  network_timeout_or_lms_slow: 'Request timed out. LMS may be slow or unreachable.',
  network_unreachable_or_lms_down: 'TCP connection refused/dropped. LMS may be down.',
  signature_invalid_check_secret: 'HMAC verification failed. Secrets out of sync.',
  signature_invalid: 'Inbound signature invalid. Possible config drift or attack.',
  lead_email_not_in_ops: 'LMS sent an email not in OPS. Likely a sync race — auto-reconciles.',
  outbound_call_never_completed: 'OPS never completed the outbound call. Restart-during-flight scenario.',
  generic_error: 'See raw payload for details.',
  unknown: 'Could not auto-categorize. View raw event.',
};

const IntegrationHealthPage = () => {
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [hours, setHours] = useState(24);
  const [activeLane, setActiveLane] = useState(null);
  const [activeStatus, setActiveStatus] = useState(null);
  const [events, setEvents] = useState([]);
  const [eventsLoading, setEventsLoading] = useState(false);
  const [detailEvent, setDetailEvent] = useState(null);

  const loadOverview = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/admin/integration-health/overview?hours=${hours}`, { withCredentials: true });
      setOverview(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load health overview');
    } finally {
      setLoading(false);
    }
  }, [hours]);

  const loadEvents = useCallback(async () => {
    if (!activeLane) { setEvents([]); return; }
    setEventsLoading(true);
    try {
      const params = new URLSearchParams({ lane: activeLane, hours: String(hours), limit: '100' });
      if (activeStatus) params.append('status', activeStatus);
      const res = await axios.get(`${API}/api/admin/integration-health/events?${params}`, { withCredentials: true });
      setEvents(res.data.events || []);
    } catch (e) {
      toast.error('Failed to load events');
    } finally {
      setEventsLoading(false);
    }
  }, [activeLane, activeStatus, hours]);

  useEffect(() => { loadOverview(); }, [loadOverview]);
  useEffect(() => { loadEvents(); }, [loadEvents]);

  useEffect(() => {
    if (!autoRefresh) return;
    const t = setInterval(() => { loadOverview(); loadEvents(); }, 30000);
    return () => clearInterval(t);
  }, [autoRefresh, loadOverview, loadEvents]);

  const openDetail = async (ev) => {
    try {
      const res = await axios.get(
        `${API}/api/admin/integration-health/event/${ev.lane}/${encodeURIComponent(ev.id)}`,
        { withCredentials: true }
      );
      setDetailEvent(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load event detail');
    }
  };

  if (loading) return <div className="p-6 text-slate-500">Loading…</div>;
  if (!overview) return <div className="p-6 text-red-600">Failed to load.</div>;

  const overallCfg = OVERALL_BADGE[overview.overall_status] || OVERALL_BADGE.healthy;

  return (
    <div className="p-6 max-w-[1600px] mx-auto" data-testid="integration-health-page">
      {/* Header */}
      <div className="flex items-start justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Activity className="w-6 h-6 text-indigo-600" />
            Integration Health
          </h1>
          <p className="text-sm text-slate-600 mt-1">
            Realtime view across Razorpay and LMS lanes. Updates every 30s when auto-refresh is on.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <select
            value={hours}
            onChange={(e) => setHours(Number(e.target.value))}
            className="input-base !w-auto !py-1.5 text-sm"
            data-testid="time-window-select"
          >
            <option value={1}>Last 1h</option>
            <option value={6}>Last 6h</option>
            <option value={24}>Last 24h</option>
            <option value={72}>Last 3d</option>
            <option value={168}>Last 7d</option>
          </select>
          <button
            onClick={() => { loadOverview(); loadEvents(); }}
            className="btn-secondary !py-1.5 text-sm flex items-center gap-1.5"
            data-testid="manual-refresh-btn"
          >
            <RefreshCw className="w-3.5 h-3.5" /> Refresh
          </button>
          <label className="flex items-center gap-2 text-sm text-slate-700">
            <input
              type="checkbox"
              checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
              data-testid="auto-refresh-toggle"
            />
            Auto-refresh
          </label>
        </div>
      </div>

      {/* Overall status banner */}
      <div className={`rounded-lg p-3 mb-6 flex items-center gap-3 ${overallCfg.bg}`}>
        <span className={`w-2.5 h-2.5 rounded-full ${overview.overall_status === 'healthy' ? 'bg-emerald-500' : overview.overall_status === 'degraded' ? 'bg-amber-500 animate-pulse' : 'bg-red-500 animate-pulse'}`} />
        <span className={`font-medium ${overallCfg.text}`}>{overallCfg.label}</span>
        <span className="text-xs text-slate-500 ml-2">window: last {overview.window_hours}h</span>
      </div>

      {/* Lane cards grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-3 mb-6">
        {overview.lanes.map((lane) => (
          <LaneCard
            key={lane.lane}
            lane={lane}
            isActive={activeLane === lane.lane}
            onClick={() => { setActiveLane(lane.lane); setActiveStatus(null); }}
          />
        ))}
      </div>

      {/* Events table */}
      {activeLane && (
        <div className="card-soft p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold text-slate-900 flex items-center gap-2">
              {LANE_KEYS.includes(activeLane) && (overview.lanes.find(l => l.lane === activeLane)?.label || activeLane)}
              <span className="text-xs text-slate-500">— {events.length} events</span>
            </h3>
            <div className="flex items-center gap-2">
              <FilterChip label="All" on={!activeStatus} onClick={() => setActiveStatus(null)} />
              <FilterChip label="Failed" on={activeStatus === 'failed'} onClick={() => setActiveStatus('failed')} cls="data-[on=true]:bg-red-50 data-[on=true]:text-red-700" />
              <FilterChip label="Warning" on={activeStatus === 'warning'} onClick={() => setActiveStatus('warning')} />
              <FilterChip label="Success" on={activeStatus === 'success'} onClick={() => setActiveStatus('success')} />
              <button onClick={() => { setActiveLane(null); setEvents([]); }} className="text-slate-400 hover:text-slate-700 ml-2" data-testid="close-events-btn">
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          {eventsLoading ? (
            <div className="py-8 text-center text-slate-500 text-sm">Loading events…</div>
          ) : events.length === 0 ? (
            <div className="py-8 text-center text-slate-500 text-sm">No events match filter.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-xs text-slate-600 uppercase tracking-wide bg-slate-50">
                  <tr>
                    <th className="px-3 py-2 text-left">Status</th>
                    <th className="px-3 py-2 text-left">When</th>
                    <th className="px-3 py-2 text-left">Type / Path</th>
                    <th className="px-3 py-2 text-left">Code</th>
                    <th className="px-3 py-2 text-left">Latency</th>
                    <th className="px-3 py-2 text-left">Probable Cause</th>
                    <th className="px-3 py-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {events.map((ev) => (
                    <tr key={`${ev.lane}-${ev.id}`} className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer" onClick={() => openDetail(ev)}>
                      <td className="px-3 py-2">
                        <span className={`inline-block w-2 h-2 rounded-full ${STATUS_DOT[ev.status] || 'bg-slate-300'}`} />
                      </td>
                      <td className="px-3 py-2 text-slate-600 text-xs">{(ev.ts || '').replace('T', ' ').substring(0, 19)}</td>
                      <td className="px-3 py-2 text-slate-700">
                        {ev.event_type || ev.path || ev.id?.substring(0, 16)}
                      </td>
                      <td className="px-3 py-2">
                        {ev.status_code !== null && ev.status_code !== undefined ? (
                          <span className={`inline-block px-1.5 py-0.5 rounded text-xs font-mono ${ev.status_code >= 200 && ev.status_code < 300 ? 'bg-emerald-50 text-emerald-700' : ev.status_code >= 400 ? 'bg-red-50 text-red-700' : 'bg-amber-50 text-amber-700'}`}>
                            {ev.status_code}
                          </span>
                        ) : <span className="text-slate-400 text-xs">—</span>}
                      </td>
                      <td className="px-3 py-2 text-slate-600 text-xs">{ev.latency_ms ? `${ev.latency_ms}ms` : '—'}</td>
                      <td className="px-3 py-2">
                        {ev.probable_cause ? (
                          <span className="text-xs text-red-700 font-medium" title={CAUSE_HINT[ev.probable_cause] || ''}>
                            {ev.probable_cause}
                          </span>
                        ) : <span className="text-emerald-600 text-xs">ok</span>}
                      </td>
                      <td className="px-3 py-2 text-slate-400">
                        <ChevronRight className="w-4 h-4" />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Event detail modal */}
      {detailEvent && (
        <EventDetailModal data={detailEvent} onClose={() => setDetailEvent(null)} />
      )}
    </div>
  );
};

const LaneCard = ({ lane, isActive, onClick }) => {
  const Icon = lane.direction === 'inbound' ? ArrowDownLeft : ArrowUpRight;
  const StatusIcon = lane.failed > 0 ? XCircle : lane.warning > 0 ? AlertTriangle : CheckCircle2;
  const statusColor = lane.failed > 0 ? 'text-red-600' : lane.warning > 0 ? 'text-amber-600' : 'text-emerald-600';

  return (
    <div
      onClick={onClick}
      className={`card-soft p-3 cursor-pointer transition border-2 ${isActive ? 'border-indigo-500' : 'border-transparent hover:border-slate-200'}`}
      data-testid={`lane-card-${lane.lane}`}
    >
      <div className="flex items-start justify-between mb-2">
        <div className="flex items-center gap-1.5">
          <Icon className="w-3.5 h-3.5 text-slate-500" />
          <span className="text-xs font-medium text-slate-700 uppercase tracking-wide">{lane.direction}</span>
        </div>
        <StatusIcon className={`w-4 h-4 ${statusColor}`} />
      </div>
      <h4 className="font-semibold text-slate-900 text-sm mb-2">{lane.label}</h4>
      <div className="flex items-end gap-2 mb-2">
        <span className="text-2xl font-bold text-slate-900">{lane.success_rate_pct}%</span>
        <span className="text-xs text-slate-500 mb-0.5">success rate</span>
      </div>
      <div className="text-xs text-slate-600 space-y-0.5">
        <div className="flex justify-between"><span>Total</span><span className="font-medium">{lane.total}</span></div>
        <div className="flex justify-between"><span className="text-red-600">Failed</span><span className="font-medium text-red-600">{lane.failed}</span></div>
        {lane.warning > 0 && <div className="flex justify-between"><span className="text-amber-600">Warning</span><span className="font-medium text-amber-600">{lane.warning}</span></div>}
        {lane.p95_latency_ms !== null && (
          <div className="flex justify-between text-slate-500"><span className="flex items-center gap-1"><Clock className="w-3 h-3" />p95</span><span>{lane.p95_latency_ms}ms</span></div>
        )}
      </div>
      {lane.top_causes.length > 0 && (
        <div className="mt-2 pt-2 border-t border-slate-100">
          <div className="text-[10px] uppercase text-slate-500 mb-1">Top causes</div>
          {lane.top_causes.slice(0, 2).map((tc) => (
            <div key={tc.cause} className="text-[11px] text-red-700 truncate" title={CAUSE_HINT[tc.cause] || ''}>
              • {tc.cause} ({tc.count})
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const FilterChip = ({ label, on, onClick }) => (
  <button
    onClick={onClick}
    data-on={on}
    className={`px-2 py-1 text-xs rounded border ${on ? 'bg-indigo-50 text-indigo-700 border-indigo-200' : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'}`}
  >
    {label}
  </button>
);

const EventDetailModal = ({ data, onClose }) => {
  const { summary, raw } = data;
  const hint = summary.probable_cause ? CAUSE_HINT[summary.probable_cause] : null;
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
          <div>
            <h3 className="font-semibold text-slate-900">Event detail · {summary.lane_label}</h3>
            <p className="text-xs text-slate-500 font-mono">{summary.id}</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X className="w-5 h-5" /></button>
        </div>
        <div className="px-5 py-4 overflow-y-auto flex-1">
          <div className="grid grid-cols-2 gap-3 mb-4 text-sm">
            <Field label="Status" value={summary.status} />
            <Field label="HTTP Code" value={summary.status_code ?? '—'} />
            <Field label="Latency" value={summary.latency_ms ? `${summary.latency_ms}ms` : '—'} />
            <Field label="When" value={summary.ts || '—'} />
          </div>
          {summary.probable_cause && (
            <div className="bg-red-50 border border-red-100 rounded-lg p-3 mb-4">
              <div className="font-semibold text-red-700 text-sm mb-1">Probable cause: {summary.probable_cause}</div>
              {hint && <div className="text-xs text-red-600">{hint}</div>}
              {summary.error_excerpt && (
                <div className="mt-2 text-xs font-mono bg-white text-red-700 p-2 rounded border border-red-200 max-h-32 overflow-auto">{summary.error_excerpt}</div>
              )}
            </div>
          )}
          <div>
            <div className="text-xs font-medium text-slate-700 mb-1">Raw event</div>
            <pre className="bg-slate-50 text-slate-800 text-xs p-3 rounded border border-slate-200 max-h-96 overflow-auto font-mono">
{JSON.stringify(raw, null, 2)}
            </pre>
          </div>
        </div>
        <div className="px-5 py-3 border-t border-slate-200 flex justify-end">
          <button onClick={onClose} className="btn-secondary">Close</button>
        </div>
      </div>
    </div>
  );
};

const Field = ({ label, value }) => (
  <div>
    <div className="text-xs text-slate-500 uppercase tracking-wide">{label}</div>
    <div className="text-slate-900 font-medium break-all">{value}</div>
  </div>
);

export default IntegrationHealthPage;
